import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * Interactive Noise Comparison
 * Generates synthetic pink-noise (London traffic proxy) routed through a
 * low-pass filter + gain whose cutoff/level are driven by a slider.
 * Left = original sash window (loud, full spectrum)
 * Right = with 10.8mm acoustic secondary glazing (80% reduction)
 */
export const NoiseComparison = () => {
  const [value, setValue] = useState(0); // 0..100
  const [playing, setPlaying] = useState(false);

  const ctxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const filterRef = useRef<BiquadFilterNode | null>(null);

  // Map slider 0..100 → reduction 0..80%
  const reductionPct = Math.round((value / 100) * 80);
  const remainingDb = Math.round(75 - (value / 100) * 45); // 75dB → 30dB

  const ensureContext = () => {
    if (ctxRef.current) return ctxRef.current;
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    const ctx: AudioContext = new AC();
    // Build ~2s of pink-ish noise buffer
    const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0;
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99765 * b0 + white * 0.099046;
      b1 = 0.96300 * b1 + white * 0.2965164;
      b2 = 0.57000 * b2 + white * 1.0526913;
      data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.15;
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 16000;
    const gain = ctx.createGain();
    gain.gain.value = 0.0;
    src.connect(filter).connect(gain).connect(ctx.destination);
    src.start();
    ctxRef.current = ctx;
    sourceRef.current = src;
    filterRef.current = filter;
    gainRef.current = gain;
    return ctx;
  };

  const applyParams = (v: number) => {
    if (!gainRef.current || !filterRef.current || !ctxRef.current) return;
    const t = ctxRef.current.currentTime;
    // Loudness drops from 0.45 → 0.09 (≈80% perceived reduction)
    const targetGain = playing ? 0.45 - (v / 100) * 0.36 : 0;
    // Cutoff moves from 16kHz down to 380Hz (muffled, like sealed glass)
    const targetFreq = 16000 - (v / 100) * 15600;
    gainRef.current.gain.linearRampToValueAtTime(targetGain, t + 0.08);
    filterRef.current.frequency.linearRampToValueAtTime(Math.max(380, targetFreq), t + 0.08);
  };

  useEffect(() => {
    applyParams(value);
  }, [value, playing]);

  useEffect(() => {
    return () => {
      try { sourceRef.current?.stop(); } catch {}
      ctxRef.current?.close();
    };
  }, []);

  const togglePlay = async () => {
    const ctx = ensureContext();
    if (ctx.state === "suspended") await ctx.resume();
    setPlaying((p) => !p);
  };

  // Visual sound wave bars (decay as slider moves right)
  const bars = Array.from({ length: 28 });
  const calmness = value / 100;

  return (
    <section className="bg-[#faf8f5] py-20 md:py-28">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="font-playfair italic text-amber-700/80 tracking-[0.25em] uppercase text-xs mb-4">
            The Difference, Heard
          </p>
          <h2 className="font-playfair text-3xl md:text-5xl text-stone-900 font-light leading-tight">
            From London Traffic to Library Silence
          </h2>
          <p className="mt-5 text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Drag the slider to hear how 10.8mm acoustic secondary glazing transforms
            a single-glazed sash on a busy London street.
          </p>
        </div>

        <div className="bg-white border border-stone-200/80 rounded-sm shadow-[0_30px_80px_-40px_rgba(0,0,0,0.25)] overflow-hidden">
          <div className="grid grid-cols-2 text-center">
            <div
              className="p-6 md:p-10 border-r border-stone-200/70 transition-colors duration-700"
              style={{ background: `rgba(120,53,15,${0.06 - calmness * 0.06})` }}
            >
              <p className="font-playfair italic text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">Before</p>
              <p className="font-playfair text-xl md:text-2xl text-stone-900">Original Sash Window</p>
              <p className="mt-2 text-sm text-stone-500">~75 dB · constant traffic</p>
            </div>
            <div
              className="p-6 md:p-10 transition-colors duration-700"
              style={{ background: `rgba(120,113,108,${0.04 + calmness * 0.06})` }}
            >
              <p className="font-playfair italic text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">After</p>
              <p className="font-playfair text-xl md:text-2xl text-stone-900">10.8mm Acoustic Glazing</p>
              <p className="mt-2 text-sm text-stone-500">~30 dB · quiet library</p>
            </div>
          </div>

          {/* Waveform */}
          <div className="h-40 md:h-52 px-8 flex items-center justify-center gap-[3px] bg-gradient-to-b from-stone-50 to-white">
            {bars.map((_, i) => {
              const seed = Math.sin(i * 1.7) * 0.5 + 0.5;
              const intensity = (1 - calmness * 0.88) * (0.35 + seed * 0.65);
              const h = 8 + intensity * 130;
              const hue = 25 - calmness * 20;
              return (
                <span
                  key={i}
                  className="block w-[3px] md:w-[5px] rounded-full transition-[height,background-color] duration-300 ease-out"
                  style={{
                    height: `${h}px`,
                    backgroundColor: `hsl(${hue}, ${50 - calmness * 40}%, ${30 + calmness * 35}%)`,
                    opacity: 0.45 + intensity * 0.55,
                  }}
                />
              );
            })}
          </div>

          {/* Slider */}
          <div className="px-6 md:px-12 pb-10 pt-6">
            <div className="flex items-center gap-5">
              <button
                onClick={togglePlay}
                aria-label={playing ? "Mute demo" : "Play demo"}
                className="shrink-0 h-12 w-12 rounded-full border border-stone-300 flex items-center justify-center hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-colors"
              >
                {playing ? <Volume2 size={18} /> : <VolumeX size={18} />}
              </button>
              <input
                type="range"
                min={0}
                max={100}
                value={value}
                onChange={(e) => setValue(parseInt(e.target.value))}
                aria-label="Acoustic glazing comparison slider"
                className="w-full accent-amber-700"
              />
            </div>
            <div className="mt-6 grid grid-cols-3 text-xs uppercase tracking-[0.2em] text-stone-500">
              <span>Single glazed</span>
              <span className="text-center font-playfair italic normal-case text-base text-amber-700 tracking-normal">
                {reductionPct}% quieter · {remainingDb} dB
              </span>
              <span className="text-right">Acoustic glazed</span>
            </div>
          </div>
        </div>

        {!playing && (
          <p className="text-center text-xs text-stone-500 mt-4 italic">
            Tap the speaker to enable audio. Best with headphones.
          </p>
        )}
      </div>
    </section>
  );
};

export default NoiseComparison;